import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useZayacSocket } from '../lib/zayacSocket';
import type { ZayacLobbyView, ZayacPhase, ZayacPlayerPublic, ZayacRoomSummary } from '@shum/shared';
import { QrCode } from '../components/QrCode';
import { ReconnectButton } from '../components/ReconnectButton';
import { ZayacScreens } from '../game/zayac/screens';
import { ZayacStyles } from '../game/zayac/styles';

type Summary = ZayacLobbyView & ZayacRoomSummary;

export function ZayacRoom() {
  const { code } = useParams<{ code: string }>();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [showReloginQr, setShowReloginQr] = useState(false);
  const { state, send, selectPlayer, createReloginQr, clearReloginQr, clearError, reconnect } = useZayacSocket(code);

  useEffect(() => {
    if (!code) return;
    setSummary(null);
    setSummaryError(null);
    api
      .getZayacRoom(code.toUpperCase())
      .then((s) => setSummary(s as Summary))
      .catch((e) => setSummaryError((e as Error).message));
  }, [code]);

  const players: ZayacPlayerPublic[] = state.lobby?.players ?? summary?.players ?? [];
  const joinUrl = `${window.location.origin}/z/${summary?.code ?? code}`;
  const qrPayload = joinUrl;
  const canStart = state.lobby?.canStart ?? summary?.canStart ?? false;
  const phase: ZayacPhase = state.phase?.phase ?? (summary?.phase as ZayacPhase) ?? 'LOBBY';
  const roundNumber = state.phase?.roundNumber ?? summary?.roundNumber ?? 0;
  const selectedId = state.selectedPlayerId;
  const me = players.find((p) => p.playerId === selectedId) ?? null;
  const canCurrentPlayerStart = Boolean(me?.isCreator && canStart);

  const openReloginQr = () => {
    if (!createReloginQr()) {
      alert('Нет связи с комнатой. Попробуйте ещё раз.');
      return;
    }
    setShowReloginQr(true);
  };
  const closeReloginQr = () => {
    setShowReloginQr(false);
    clearReloginQr();
  };

  if (summaryError) {
    return (
      <Center status={state.status} onReconnect={reconnect}>
        <ZayacStyles />
        <div className="zy-error">
          <span className="zy-label">Комната не найдена</span>
          <h1>Такой комнаты нет</h1>
          <p>{summaryError}</p>
          <Link to="/play/zayac/create" className="btn-primary lg">
            Создать новую →
          </Link>
        </div>
      </Center>
    );
  }

  if (!summary) {
    return (
      <Center status={state.status} onReconnect={reconnect}>
        <ZayacStyles />
        <div className="zy-connecting">
          <span className="zy-label">Подключаемся…</span>
          <div className="zy-spinner" />
        </div>
      </Center>
    );
  }

  const disconnected = state.status === 'closed';

  if (state.replaced) {
    return (
      <Center status={state.status} onReconnect={reconnect}>
        <ZayacStyles />
        <div className="zy-error">
          <span className="zy-label">Сессия перенесена</span>
          <h1>Ваш игрок открыт на другом устройстве</h1>
          <p>Выберите себя заново на этом устройстве, чтобы продолжить.</p>
          <button className="btn-primary lg" onClick={() => clearError()}>
            Понятно
          </button>
        </div>
      </Center>
    );
  }

  if (!me) {
    return (
      <Center status={state.status} onReconnect={reconnect}>
        <ZayacStyles />
        <div className="zy-idsel">
          <span className="zy-label">Комната {summary.code}</span>
          <h1>Выберите своё имя</h1>
          <div className="zy-name-grid">
            {players.map((p) => (
              <button key={p.playerId} className="zy-name-btn" onClick={() => selectPlayer(p.playerId)} data-status={p.status}>
                <span className="nm">{p.name}</span>
                <span className="st">
                  {p.status === 'connected' ? 'переподключиться' : p.status === 'disconnected' ? 'отключился' : 'войти'}
                </span>
                {p.isCreator && <span className="star">★</span>}
              </button>
            ))}
          </div>
          {state.error && <div className="zy-err">{state.error.message}</div>}
          {disconnected && <div className="zy-banner">Нет связи. Переподключаемся…</div>}
        </div>
      </Center>
    );
  }

  if (phase === 'LOBBY') {
    return (
      <Center status={state.status} onReconnect={reconnect}>
        <ZayacStyles />
        <div className="zy-lobby">
          <div className="zy-lobby-left">
            <span className="zy-label">Лобби · ждём игроков</span>
            <h1>Комната «{summary.code}»</h1>
            <p className="zy-muted">Покажите этот QR или отправьте ссылку в чат.</p>
            <QrCode value={qrPayload} />
            <div className="zy-join-url mono">{joinUrl}</div>
          </div>
          <div className="zy-lobby-right">
            <div className="zy-panel">
              <div className="zy-panel-head">
                <span className="zy-label">Игроки · {players.length}</span>
                <span className={`zy-pill ${canStart ? 'on' : ''}`}>{canStart ? 'все на месте' : 'ждём…'}</span>
              </div>
              <div className="zy-list">
                {players.map((p) => (
                  <div className="zy-lp" key={p.playerId} data-status={p.status}>
                    <span className="zy-lpn">
                      {p.name} {p.isCreator && <span className="star" style={{ color: 'var(--accent)' }}>★</span>}
                    </span>
                    <span className="zy-lps">{statusLabel(p.status)}</span>
                  </div>
                ))}
              </div>
              <button type="button" className="zy-relogin-button" onClick={openReloginQr}>
                Перелогиниться по QR
              </button>
              {me?.isCreator && (
                <button
                  className="btn-primary block lg"
                  disabled={!canCurrentPlayerStart}
                  onClick={() => {
                    if (!send({ type: 'zayac.start' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
                  }}
                >
                  {canStart ? 'Начать игру →' : 'Ждём подключения всех'}
                </button>
              )}
              {!canStart && <p className="zy-hint">Нужно, чтобы все выбрали имя. Кто-то отключился — переподключится через QR.</p>}
              {canStart && !me?.isCreator && <p className="zy-hint">Старт доступен только отмеченному ведущему.</p>}
              {state.error && <div className="zy-err">{state.error.message}</div>}
            </div>
          </div>
          {disconnected && <div className="zy-banner">Нет связи. Переподключаемся…</div>}
          {showReloginQr && (
            <ReloginQrPanel code={summary.code} invite={state.reloginQr} onClose={closeReloginQr} />
          )}
        </div>
      </Center>
    );
  }

  return (
    <Center>
      <ZayacStyles />
      <div className="zy-game">
        {disconnected && <div className="zy-banner">Нет связи. Переподключаемся…</div>}
        <div className="zy-topbar">
          <span className="zy-label">Раунд {state.privateState?.roundNumber ?? roundNumber} / {summary.settings.rounds}</span>
          <span className="zy-label">{phaseLabel(phase)}</span>
          {me && <span className="zy-pill">{me.name} · {me.score} очк.</span>}
          <button type="button" className="zy-relogin-button compact" onClick={openReloginQr}>
            QR для перевхода
          </button>
        </div>
        <ZayacScreens state={state} send={send} privateState={state.privateState} />
        {showReloginQr && (
          <ReloginQrPanel code={summary.code} invite={state.reloginQr} onClose={closeReloginQr} />
        )}
      </div>
    </Center>
  );
}

function ReloginQrPanel({
  code,
  invite,
  onClose,
}: {
  code: string;
  invite: { playerId: string; playerName: string; token: string; expiresAt: string } | null;
  onClose: () => void;
}) {
  const url = invite ? reloginQrUrl(code, invite.token) : null;
  return (
    <div className="zy-relogin-overlay" role="dialog" aria-modal="true" aria-label="QR-код для повторного входа">
      <div className="zy-relogin-panel">
        <button type="button" className="zy-relogin-close" onClick={onClose} aria-label="Закрыть">×</button>
        <span className="zy-label">Перенос игрока на другое устройство</span>
        <h2>{invite ? invite.playerName : 'Готовим QR…'}</h2>
        {url && invite ? (
          <>
            <QrCode value={url} size={220} />
            <p>Отсканируйте на другом устройстве. Вход произойдёт сразу под этим игроком, а текущая сессия закроется.</p>
            <span className="zy-label">Одноразовый · действует до {new Date(invite.expiresAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
          </>
        ) : (
          <div className="zy-spinner" />
        )}
      </div>
    </div>
  );
}

function reloginQrUrl(code: string, token: string): string {
  const url = new URL(`/z/${encodeURIComponent(code)}`, window.location.origin);
  url.searchParams.set('relogin', token);
  return url.toString();
}

function Center({
  children,
  status,
  onReconnect,
}: {
  children: React.ReactNode;
  status?: 'connecting' | 'open' | 'closed';
  onReconnect?: () => void;
}) {
  return (
    <div className="zy-root">
      {status && onReconnect && <ReconnectButton status={status} onReconnect={onReconnect} />}
      <Header />
      {children}
    </div>
  );
}

function Header() {
  return (
    <header className="bar">
      <div className="bar-row" style={{ padding: '14px 32px' }}>
        <Link to="/" className="logo">
          <span className="logo-mark">Ш</span>
          <span className="logo-name">Шум.клуб</span>
        </Link>
        <Link to="/rooms" className="zy-label" style={{ textDecoration: 'none' }}>
          Войти по коду
        </Link>
      </div>
    </header>
  );
}

function statusLabel(s: ZayacPlayerPublic['status']): string {
  switch (s) {
    case 'connected':
      return 'в игре';
    case 'disconnected':
      return 'отключился';
    case 'replaced':
      return 'заменён';
    default:
      return 'не вошёл';
  }
}

function phaseLabel(phase: ZayacPhase): string {
  const map: Record<ZayacPhase, string> = {
    LOBBY: 'Лобби',
    SECRET_DISTRIBUTION: 'Раздача слов',
    ASSOCIATION: 'Ассоциации',
    DISCUSSION: 'Обсуждение',
    VOTING: 'Голосование',
    REVEAL: 'Раскрытие',
    ROUND_END: 'Конец раунда',
    GAME_OVER: 'Игра окончена',
  };
  return map[phase] ?? phase;
}
